import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Paper, Button, Alert, IconButton, Tooltip, Stack, Typography, TextField, InputAdornment,
  Dialog, DialogContent, DialogContentText, DialogActions, FormControlLabel, Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery, useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { PageHeader } from "@/components/PageHeader";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";

export interface MasterRow { id: string }

/**
 * What a row is matched against when the master doesn't supply its own `searchText`. Every own
 * string/number field counts — code, name, description, unit and so on — which is what an operator
 * scanning a master expects. Ids are skipped: they are never displayed, and a raw uuid fragment
 * matching a typed word would look like a bug.
 */
const defaultSearchText = (row: MasterRow) =>
  Object.entries(row)
    .filter(([k, v]) => k !== "id" && !k.endsWith("Id") && (typeof v === "string" || typeof v === "number"))
    .map(([, v]) => String(v))
    .join(" ");

/**
 * Shared listing scaffold for the diagnostics masters: grid + edit/delete icons, a "show inactive"
 * toggle, and a confirm dialog that spells out what a soft delete does. Every master's list screen is
 * this component with a different column set, so the delete semantics can't drift between them.
 *
 * `columns` should omit the trailing actions column — it's appended here.
 */
export function MasterListPage<T extends MasterRow>({
  breadcrumbs, title, subtitle, queryKey, listFn, deleteFn, columns, basePath,
  labelOf, deleteNote, newLabel, filters, searchPlaceholder, searchText,
}: {
  breadcrumbs: string[];
  title: string;
  subtitle?: string;
  queryKey: QueryKey;
  listFn: (includeInactive: boolean) => Promise<T[]>;
  deleteFn: (id: string) => Promise<unknown>;
  columns: GridColDef<T>[];
  /** Route prefix; the screen links to `${basePath}/new` and `${basePath}/:id/edit`. */
  basePath: string;
  labelOf: (row: T) => string;
  /** What blocks the delete, shown in the confirm dialog. */
  deleteNote: string;
  newLabel: string;
  /** Optional extra filter controls rendered next to the inactive toggle. */
  filters?: ReactNode;
  /** Hint under the search box, e.g. "Search by code or name". */
  searchPlaceholder?: string;
  /**
   * Text a row is matched against. Override when a column shows something the row itself doesn't
   * carry — a lab test's department name is resolved from another query, so searching "Biochem"
   * only works if that resolved label is folded in here.
   */
  searchText?: (row: T) => string;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canManage = useAuth(s => s.hasPermission("diagnostics.catalog.manage"));

  const [includeInactive, setIncludeInactive] = useState(true);
  const [search, setSearch] = useState("");
  const [pendingDelete, setPendingDelete] = useState<T | null>(null);

  const query = useQuery({
    queryKey: [...(Array.isArray(queryKey) ? queryKey : [queryKey]), includeInactive],
    queryFn: () => listFn(includeInactive),
  });

  // These endpoints return the whole master in one call, so the filter runs here rather than
  // round-tripping per keystroke. Terms are AND-ed, so "urine steril" narrows the way a typist
  // expects instead of only matching that exact adjacent phrase.
  const rows = useMemo(() => {
    const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return query.data ?? [];
    return (query.data ?? []).filter(row => {
      const haystack = (searchText ? searchText(row) : defaultSearchText(row)).toLowerCase();
      return terms.every(t => haystack.includes(t));
    });
  }, [query.data, search, searchText]);

  const remove = useMutation({
    meta: { successMessage: `${title} entry deleted` },
    mutationFn: (id: string) => deleteFn(id),
    onSuccess: () => {
      setPendingDelete(null);
      qc.invalidateQueries({ queryKey });
    },
  });

  const allColumns: GridColDef<T>[] = [
    ...columns,
    {
      field: "actions", headerName: "", width: 110, sortable: false, filterable: false,
      align: "right", headerAlign: "right",
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title={canManage ? "Edit" : "You lack diagnostics.catalog.manage"}>
            <span>
              <IconButton size="small" disabled={!canManage}
                onClick={() => navigate(`${basePath}/${p.row.id}/edit`)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canManage ? "Delete" : "You lack diagnostics.catalog.manage"}>
            <span>
              <IconButton size="small" color="error" disabled={!canManage}
                onClick={() => setPendingDelete(p.row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={title}
        subtitle={subtitle}
        actions={canManage ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`${basePath}/new`)}>
            {newLabel}
          </Button>
        ) : undefined}
      />

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}
          sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <TextField
            placeholder={searchPlaceholder ?? "Search by code or name"}
            size="small" value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 260 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <FormControlLabel
            control={<Switch checked={includeInactive} onChange={(_, v) => setIncludeInactive(v)} />}
            label="Show inactive"
          />
          {filters}
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {remove.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(remove.error)}</Alert>}
        <Box sx={{ height: 540 }}>
          <DataGrid rows={rows} columns={allColumns} loading={query.isLoading} getRowId={r => r.id}
            disableRowSelectionOnClick pageSizeOptions={[25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} />
        </Box>
      </Paper>

      <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)} maxWidth="xs" fullWidth>
        <ClosableDialogTitle onClose={() => setPendingDelete(null)}>Confirm delete</ClosableDialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{pendingDelete ? labelOf(pendingDelete) : ""}</strong>?
          </DialogContentText>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            This is a soft delete — the record is hidden, not destroyed. {deleteNote}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={remove.isPending}
            onClick={() => pendingDelete && remove.mutate(pendingDelete.id)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
